'use client';

import { createClient } from '@/lib/supabase/client';

interface CityService {
  id: string;
  cityId: string;
  serviceId: string;
  isAvailable: boolean;
  citySpecificPricing?: string;
  citySpecificDescription?: string;
  serviceFeatures: string[];
  coverageDetails?: string;
  estimatedDeliveryTime?: string;
  specialNotes?: string;
  bookNowText?: string;
  bookNowLink?: string;
  displayOrder: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface CityServiceWithDetails extends CityService {
  serviceName?: string;
  serviceSlug?: string;
  cityName?: string;
  citySlug?: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const cityServiceService = {
  async getAll(): Promise<CityServiceWithDetails[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_services')
        .select(`
          *,
          city_pages!inner(name, slug),
          services!inner(title, slug)
        `)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        cityId: row.city_id,
        serviceId: row.service_id,
        isAvailable: row.is_available,
        citySpecificPricing: row.city_specific_pricing,
        citySpecificDescription: row.city_specific_description,
        serviceFeatures: row.service_features || [],
        coverageDetails: row.coverage_details,
        estimatedDeliveryTime: row.estimated_delivery_time,
        specialNotes: row.special_notes,
        bookNowText: row.book_now_text,
        bookNowLink: row.book_now_link,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        cityName: row.city_pages?.name,
        citySlug: row.city_pages?.slug,
        serviceName: row.services?.title,
        serviceSlug: row.services?.slug,
      }));
    } catch (error: any) {
      console.error('Get city services error:', error.message);
      throw error;
    }
  },

  async getByCityId(cityId: string): Promise<CityServiceWithDetails[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('city_services')
        .select(`
          *,
          services!inner(title, slug, description, featured_image_url)
        `)
        .eq('city_id', cityId)
        .eq('is_available', true)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        cityId: row.city_id,
        serviceId: row.service_id,
        isAvailable: row.is_available,
        citySpecificPricing: row.city_specific_pricing,
        citySpecificDescription: row.city_specific_description,
        serviceFeatures: row.service_features || [],
        coverageDetails: row.coverage_details,
        estimatedDeliveryTime: row.estimated_delivery_time,
        specialNotes: row.special_notes,
        bookNowText: row.book_now_text,
        bookNowLink: row.book_now_link,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        serviceName: row.services?.title,
        serviceSlug: row.services?.slug,
      }));
    } catch (error: any) {
      console.error('Get city services by city error:', error.message);
      throw error;
    }
  },

  async getByCityIdAdmin(cityId: string): Promise<CityServiceWithDetails[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('city_services')
        .select(`
          *,
          services!inner(title, slug, description, featured_image_url)
        `)
        .eq('city_id', cityId)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        cityId: row.city_id,
        serviceId: row.service_id,
        isAvailable: row.is_available,
        citySpecificPricing: row.city_specific_pricing,
        citySpecificDescription: row.city_specific_description,
        serviceFeatures: row.service_features || [],
        coverageDetails: row.coverage_details,
        estimatedDeliveryTime: row.estimated_delivery_time,
        specialNotes: row.special_notes,
        bookNowText: row.book_now_text,
        bookNowLink: row.book_now_link,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        serviceName: row.services?.title,
        serviceSlug: row.services?.slug,
      }));
    } catch (error: any) {
      console.error('Get city services by city (admin) error:', error.message);
      throw error;
    }
  },

  async getByCitySlug(citySlug: string): Promise<CityServiceWithDetails[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('city_services')
        .select(`
          *,
          city_pages!inner(slug),
          services!inner(title, slug, description, featured_image_url)
        `)
        .eq('city_pages.slug', citySlug)
        .eq('is_available', true)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        cityId: row.city_id,
        serviceId: row.service_id,
        isAvailable: row.is_available,
        citySpecificPricing: row.city_specific_pricing,
        citySpecificDescription: row.city_specific_description,
        serviceFeatures: row.service_features || [],
        coverageDetails: row.coverage_details,
        estimatedDeliveryTime: row.estimated_delivery_time,
        specialNotes: row.special_notes,
        bookNowText: row.book_now_text,
        bookNowLink: row.book_now_link,
        displayOrder: row.display_order,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        serviceName: row.services?.title,
        serviceSlug: row.services?.slug,
      }));
    } catch (error: any) {
      console.error('Get city services by city slug error:', error.message);
      throw error;
    }
  },

  async create(cityService: Omit<CityService, 'id' | 'createdAt' | 'updatedAt'>): Promise<CityService> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_services')
        .insert({
          city_id: cityService.cityId,
          service_id: cityService.serviceId,
          is_available: cityService.isAvailable,
          city_specific_pricing: cityService.citySpecificPricing,
          city_specific_description: cityService.citySpecificDescription,
          service_features: cityService.serviceFeatures,
          coverage_details: cityService.coverageDetails,
          estimated_delivery_time: cityService.estimatedDeliveryTime,
          special_notes: cityService.specialNotes,
          book_now_text: cityService.bookNowText || 'Book Now',
          book_now_link: cityService.bookNowLink || '/user-signup',
          display_order: cityService.displayOrder,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.error('Create city service error:', error.message);
          throw new Error(error.message || 'Failed to create city service');
        }
      }

      return {
        id: data.id,
        cityId: data.city_id,
        serviceId: data.service_id,
        isAvailable: data.is_available,
        citySpecificPricing: data.city_specific_pricing,
        citySpecificDescription: data.city_specific_description,
        serviceFeatures: data.service_features || [],
        coverageDetails: data.coverage_details,
        estimatedDeliveryTime: data.estimated_delivery_time,
        specialNotes: data.special_notes,
        bookNowText: data.book_now_text,
        bookNowLink: data.book_now_link,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create city service error:', error.message);
      throw error;
    }
  },

  async update(id: string, updates: Partial<Omit<CityService, 'id' | 'createdAt' | 'updatedAt'>>): Promise<CityService | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      
      if (updates.cityId !== undefined) updateData.city_id = updates.cityId;
      if (updates.serviceId !== undefined) updateData.service_id = updates.serviceId;
      if (updates.isAvailable !== undefined) updateData.is_available = updates.isAvailable;
      if (updates.citySpecificPricing !== undefined) updateData.city_specific_pricing = updates.citySpecificPricing;
      if (updates.citySpecificDescription !== undefined) updateData.city_specific_description = updates.citySpecificDescription;
      if (updates.serviceFeatures !== undefined) updateData.service_features = updates.serviceFeatures;
      if (updates.coverageDetails !== undefined) updateData.coverage_details = updates.coverageDetails;
      if (updates.estimatedDeliveryTime !== undefined) updateData.estimated_delivery_time = updates.estimatedDeliveryTime;
      if (updates.specialNotes !== undefined) updateData.special_notes = updates.specialNotes;
      if (updates.bookNowText !== undefined) updateData.book_now_text = updates.bookNowText;
      if (updates.bookNowLink !== undefined) updateData.book_now_link = updates.bookNowLink;
      if (updates.displayOrder !== undefined) updateData.display_order = updates.displayOrder;

      const { data, error } = await supabase
        .from('city_services')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Update city service error:', error.message);
        throw new Error(error.message || 'Failed to update city service');
      }

      return {
        id: data.id,
        cityId: data.city_id,
        serviceId: data.service_id,
        isAvailable: data.is_available,
        citySpecificPricing: data.city_specific_pricing,
        citySpecificDescription: data.city_specific_description,
        serviceFeatures: data.service_features || [],
        coverageDetails: data.coverage_details,
        estimatedDeliveryTime: data.estimated_delivery_time,
        specialNotes: data.special_notes,
        bookNowText: data.book_now_text,
        bookNowLink: data.book_now_link,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update city service error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('city_services')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
        }
      }
    } catch (error: any) {
      console.error('Delete city service error:', error.message);
      throw error;
    }
  },

  async copyServicesFromCity(sourceCityId: string, targetCityId: string): Promise<CityService[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Fetch all services from source city
      const sourceServices = await this.getByCityId(sourceCityId);
      
      if (sourceServices.length === 0) {
        return [];
      }

      // Create new city service assignments for target city
      const copiedServices: CityService[] = [];
      
      for (const service of sourceServices) {
        const newService = {
          city_id: targetCityId,
          service_id: service.serviceId,
          is_available: service.isAvailable,
          city_specific_pricing: service.citySpecificPricing,
          city_specific_description: service.citySpecificDescription,
          service_features: service.serviceFeatures,
          coverage_details: service.coverageDetails,
          estimated_delivery_time: service.estimatedDeliveryTime,
          special_notes: service.specialNotes,
          display_order: service.displayOrder,
          created_by: user.id,
        };

        const { data, error } = await supabase
          .from('city_services')
          .insert(newService)
          .select()
          .single();

        if (error) {
          // Skip if duplicate (city_id, service_id) already exists
          if (error.code === '23505') {
            console.log(`Service ${service.serviceId} already exists for target city, skipping`);
            continue;
          }
          
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            continue;
          }
        }

        if (data) {
          copiedServices.push({
            id: data.id,
            cityId: data.city_id,
            serviceId: data.service_id,
            isAvailable: data.is_available,
            citySpecificPricing: data.city_specific_pricing,
            citySpecificDescription: data.city_specific_description,
            serviceFeatures: data.service_features || [],
            coverageDetails: data.coverage_details,
            estimatedDeliveryTime: data.estimated_delivery_time,
            specialNotes: data.special_notes,
            displayOrder: data.display_order,
            createdBy: data.created_by,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
          });
        }
      }

      return copiedServices;
    } catch (error: any) {
      console.error('Copy city services error:', error.message);
      throw error;
    }
  },

  async upsertByCityAndService(cityService: Omit<CityService, 'id' | 'createdAt' | 'updatedAt'>): Promise<CityService> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('city_services')
        .upsert(
          {
            city_id: cityService.cityId,
            service_id: cityService.serviceId,
            is_available: cityService.isAvailable,
            city_specific_pricing: cityService.citySpecificPricing,
            city_specific_description: cityService.citySpecificDescription,
            service_features: cityService.serviceFeatures,
            coverage_details: cityService.coverageDetails,
            estimated_delivery_time: cityService.estimatedDeliveryTime,
            special_notes: cityService.specialNotes,
            book_now_text: cityService.bookNowText || 'Book Now',
            book_now_link: cityService.bookNowLink || '/user-signup',
            display_order: cityService.displayOrder,
            created_by: user.id,
          },
          { onConflict: 'city_id,service_id' }
        )
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.error('Upsert city service error:', error.message);
          throw new Error(error.message || 'Failed to save city service');
        }
      }

      return {
        id: data.id,
        cityId: data.city_id,
        serviceId: data.service_id,
        isAvailable: data.is_available,
        citySpecificPricing: data.city_specific_pricing,
        citySpecificDescription: data.city_specific_description,
        serviceFeatures: data.service_features || [],
        coverageDetails: data.coverage_details,
        estimatedDeliveryTime: data.estimated_delivery_time,
        specialNotes: data.special_notes,
        bookNowText: data.book_now_text,
        bookNowLink: data.book_now_link,
        displayOrder: data.display_order,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Upsert city service error:', error.message);
      throw error;
    }
  },
};
